import React, { useEffect, useRef, useState } from "react";
import { ExclusivityCheckFields, ExclusivityCheckResult, fetchExclusivityConflicts } from "./utils";
import { Loading } from "components/panels";
import { Modal } from "components";
import { ActiveShowCard, ConflictingShowCard } from "./ShowCard";
import moment from "moment";
import { GoogleMapConflictingShows, GoogleMapConflictingShowsRef } from "components/GoogleMap/GoogleMapConflictingShows";

interface ExclusivityConflictsModalProps {
  isOpen: boolean
  handleClose: () => void
  activeShow: server.dto.Show
  exclusivityCheckFields: ExclusivityCheckFields
}

export const ExclusivityConflictsModal = (props: ExclusivityConflictsModalProps) => {
  const [exclusivityConflictResult, setExclusivityConflictResult] = useState<ExclusivityCheckResult>();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedShowId, setSelectedShowId] = useState<number>(-1);
  const [selectedLocation, setSelectedLocation] = useState<server.dto.Geo>(undefined);
  const [selectedExclusivityClause, setSelectedExclusivityClause] = useState<server.dto.ExclusivityClause>(undefined);
  const [hoveredShowId, setHoveredShowId] = useState<number>(-1);
  const [hoveredLocation, setHoveredLocation] = useState<server.dto.Geo>(undefined);
  const mapRef = useRef<GoogleMapConflictingShowsRef>(null);

  useEffect(() => {
    const fetch = async () => {
      setIsLoading(true);
      const r = await fetchExclusivityConflicts(props.exclusivityCheckFields, false, exclusivityConflictResult);
      setExclusivityConflictResult(r);
      setIsLoading(false);
    }
    if (props.isOpen) {
      fetch();
    }
  }, [props.isOpen]);

  const onMapRendered = () => {
    onSelectedLocationChange(props.activeShow.venueGeoLocation, props.activeShow.showId, props.exclusivityCheckFields.exclusivityClause);
  }

  const onHoveredLocationChange = (geo: server.dto.Geo, showId: number) => {
    setHoveredLocation(geo);
    setHoveredShowId(showId);
  }

  const onSelectedLocationChange = (geo: server.dto.Geo, showId: number, clause: server.dto.ExclusivityClause) => {
    setSelectedShowId(showId);
    setSelectedLocation(geo);
    setSelectedExclusivityClause(clause);
  }

  if (!props.isOpen) {
    return null;
  }

  const conflictingShows = exclusivityConflictResult?.exclusivityConflicts ?? [];

  return <Modal
    isOpen={props.isOpen}
    handleClose={props.handleClose}
    title={RESX.Exclusivity.resxExclusivity}
    width="huge"
  >
    {isLoading && <Loading />}
    {!isLoading && <div className="flex" style={{
      height: "50vh",
      gap: 40,
    }}>
      <div style={{ width: 400 }} className="scrollbox-auto">
        <div className="flex-column gap-larger">
          <div>
            <ActiveShowCard
              show={props.activeShow}
              clause={props.exclusivityCheckFields.exclusivityClause}
              isSelected={selectedShowId === props.activeShow.showId}
              onClick={() => onSelectedLocationChange(props.activeShow.venueGeoLocation, props.activeShow.showId, props.exclusivityCheckFields.exclusivityClause)}
              isHovered={hoveredShowId === props.activeShow.showId}
              onHover={() => onHoveredLocationChange(props.activeShow.venueGeoLocation, props.activeShow.showId)}
              onHoverEnd={() => onHoveredLocationChange(undefined, -1)}
            />
          </div>
          {conflictingShows.length > 0 &&
            <div>
              <h3 className="margin-bottom">{RESX.Exclusivity.ExclusivityConflicts}</h3>
              <div className="flex-column gap">
                {conflictingShows.map((conflict) =>
                  <ConflictingShowCard
                    key={conflict.showId}
                    show={conflict}
                    clause={conflict.exclusivityClause}
                    compareToShowDate={moment.utc(props.activeShow.dateTime.startDisplay)}
                    isSelected={selectedShowId === conflict.showId}
                    onClick={() => onSelectedLocationChange(conflict.venue.location, conflict.showId, conflict.exclusivityClause)}
                    isHovered={hoveredShowId === conflict.showId}
                    onHover={() => onHoveredLocationChange(conflict.venue.location, conflict.showId)}
                    onHoverEnd={() => onHoveredLocationChange(undefined, -1)}
                  />
                )}
              </div>
            </div>}
        </div>
      </div>
      <div className="grow1">
        <GoogleMapConflictingShows
          ref={mapRef}
          activeShow={props.activeShow}
          activeShowClause={props.exclusivityCheckFields.exclusivityClause}
          conflictingShows={conflictingShows}
          selectedLocation={selectedLocation}
          selectedClause={selectedExclusivityClause}
          onSelectedLocationChange={onSelectedLocationChange}
          hoveredLocation={hoveredLocation}
          onHoveredLocationChange={onHoveredLocationChange}
          onMapRendered={onMapRendered}
        />
      </div>
    </div>}
  </Modal>
}